import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CALCULATOR_BLOG_URLS } from 'src/utils/const';
import { User } from 'src/interfaces/iuser';
import { checkUserTailorMarketAccess, isDemoMarket } from 'src/utils/functions';

export const useIsDisabled = () => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const search = useSelector(state => (state as any).dashboard.search);
  const isUserAllowed = useSelector(state => (state as any).dashboard.isUserAllowed);

  const userTailorMarketAccess = useMemo(() => checkUserTailorMarketAccess(user, search), [
    user,
    search,
  ]);

  return !isUserAllowed || !userTailorMarketAccess;
};

export const useCanAccessExpertPlan = () => {
  const isDisabled = useIsDisabled();
  const user: User = useSelector(state => (state as any).dashboard.user);

  if (!isDisabled) return true;
  
  return ['expert', 'scale'].includes(user.subscriptionPlan);
};

export const useCanAccessScalePlan = () => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  return user.subscriptionPlan === 'scale';
};

export const useCannotAccessMainKPI = (timePeriod?: number) => {
  const isDisabled = useIsDisabled();
  const canAccessExpertPlan = useCanAccessExpertPlan();

  if (isDemoMarket()) return false;

  if (!isDisabled) return false;

  if (!canAccessExpertPlan && timePeriod && timePeriod === 36) return true;

  return false;
};

export const useCannotAccessFilter = currentFilterTab => {
  const isDisabled = useIsDisabled();
  const canAccessExpertPlan = useCanAccessExpertPlan();

  if (isDemoMarket()) return false;

  if (!isDisabled) return false;

  if (!canAccessExpertPlan && currentFilterTab != 'listing') return true;

  return false;
};

export const useCannotAccessHosts = dashboardLayoutView => {
  const isDisabled = useIsDisabled();
  const canAccessExpertPlan = useCanAccessExpertPlan();

  if (isDemoMarket()) return false;

  if (isDisabled || !canAccessExpertPlan) return true;

  return false;
};

export const useCannotAccessExpertPlan = () => {
  const isDisabled = useIsDisabled();
  const canAccessExpertPlan = useCanAccessExpertPlan();

  if (isDemoMarket()) return false;

  if (!isDisabled) return false;

  if (!canAccessExpertPlan) return true;

  return false;
};

export const useSubmarketBlur = () => {
  const search = useSelector(state => (state as any).dashboard.search);
  const isSubMarket = search.data.isSubmarket;
  const canAccessExpertPlan = useCanAccessExpertPlan();

  return isSubMarket && !canAccessExpertPlan;
};

export const useForcedPlanForMainKPI = timePeriod => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const search = useSelector(state => (state as any).dashboard.search);
  const userTailorMarketAccess = useMemo(() => checkUserTailorMarketAccess(user, search), [
    user,
    search,
  ]);
  if (!userTailorMarketAccess) {
    return 'expert';
  } else if (user.subscriptionPlan === 'starter') {
    return 'expert';
  } else if (timePeriod === 36 && !['expert', 'scale'].includes(user.subscriptionPlan)) {
    return 'expert';
  } else {
    return '';
  }
};
